import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { useAuth } from "@/components/ProtectedRoute";
import { Plus, Trash2, Tag, Gamepad2, Monitor, AlertTriangle, Database, Edit2, Trophy, Power, Unlink, Loader2, User, Users, Download, CheckCircle2, Info, ChevronDown, ChevronUp, Wrench, Clock, Star, Archive, ListChecks, Tags, Bug, MessageSquare, AlertCircle, Copy, ExternalLink, Mail, Layers, X, MoveRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import TagBadge from "@/components/TagBadge";
import PlatformBadge from "@/components/PlatformBadge";
import ProGate from "@/components/ProGate";

const API = `${getApiUrl()}/api`;

const PRESET_COLORS = [
  "#EF4444", "#F59E0B", "#10B981", "#3B82F6",
  "#8B5CF6", "#EC4899", "#14B8A6", "#6366F1",
  "#107C10", "#003087", "#66C0F4", "#FFFFFF",
];

// Gamertags Section Component for Multiplayer identity
function GamertagsSection() {
  const [gamertags, setGamertags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/multiplayer/gamertags`),
      axios.get(`${API}/platforms`),
    ]).then(([gtRes, pRes]) => {
      setPlatforms(pRes.data);
      // Ensure all platforms have an entry
      const existing = gtRes.data || [];
      const all = pRes.data.map(p => {
        const found = existing.find(g => g.platform === p.id);
        return { platform: p.id, gamertag: found?.gamertag || "" };
      });
      setGamertags(all);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleChange = (platform, value) => {
    setGamertags(prev => prev.map(g => g.platform === platform ? { ...g, gamertag: value } : g));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const filtered = gamertags.filter(g => g.gamertag.trim());
      await axios.put(`${API}/multiplayer/gamertags`, filtered);
      toast.success("Gamertags saved");
    } catch {
      toast.error("Failed to save gamertags");
    } finally {
      setSaving(false);
    }
  };

  const platformName = (id) => platforms.find(p => p.id === id)?.name || id;

  return (
    <Card className="bg-[#0A0A0A] border-white/5">
      <CardHeader>
        <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
          <Gamepad2 className="w-5 h-5 text-blue-400" />
          Gamertags
        </CardTitle>
        <CardDescription className="text-white/40">
          Store your gamertags for each platform. These can be shared with other players on the Multiplayer page.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-white/20" /></div>
        ) : (
          <div className="space-y-3">
            {gamertags.map(g => (
              <div key={g.platform} className="flex items-center gap-3">
                <div className="w-28 flex items-center gap-1.5">
                  <PlatformBadge platform={g.platform} size="xs" showLabel={false} />
                  <span className="text-white/60 text-sm">{platformName(g.platform)}</span>
                </div>
                <Input
                  data-testid={`gamertag-${g.platform}`}
                  placeholder={`${platformName(g.platform)} gamertag...`}
                  value={g.gamertag}
                  onChange={e => handleChange(g.platform, e.target.value)}
                  className="bg-white/5 border-white/10 text-white flex-1"
                />
              </div>
            ))}
            <Button
              data-testid="save-gamertags-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 mt-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save Gamertags
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const [tags, setTags] = useState([]);
  const [tagUsage, setTagUsage] = useState({});
  const [gameTags, setGameTags] = useState([]);
  const [gameTagUsage, setGameTagUsage] = useState({});
  const [gameTagDialogOpen, setGameTagDialogOpen] = useState(false);
  const [newGameTag, setNewGameTag] = useState({ name: "", color: "#6366F1" });
  const [platforms, setPlatforms] = useState([]);
  const [systems, setSystems] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Xbox state
  const [xboxStatus, setXboxStatus] = useState({ configured: false, gamertag: null, xuid: null });
  const [xboxOAuthStatus, setXboxOAuthStatus] = useState({ authenticated: false, gamertag: null });
  const [xboxSyncing, setXboxSyncing] = useState(false);
  const [xboxSyncResult, setXboxSyncResult] = useState(null);
  const [xboxOAuthLoading, setXboxOAuthLoading] = useState(false);
  const [showXboxSetup, setShowXboxSetup] = useState(false);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  
  // Steam state
  const [steamStatus, setSteamStatus] = useState({ configured: false, steamId: null, steamName: null });
  const [steamIdInput, setSteamIdInput] = useState("");
  const [steamSaving, setSteamSaving] = useState(false);
  const [steamClearing, setSteamClearing] = useState(false);
  const [steamSyncing, setSteamSyncing] = useState(false);
  const [steamSyncResult, setSteamSyncResult] = useState(null);
  
  // PlayStation state
  const [psnStatus, setPsnStatus] = useState({ configured: false, onlineId: null });
  const [npssoInput, setNpssoInput] = useState("");
  const [psnSaving, setPsnSaving] = useState(false);
  const [psnClearing, setPsnClearing] = useState(false);
  const [psnSyncing, setPsnSyncing] = useState(false);
  const [psnSyncResult, setPsnSyncResult] = useState(null);
  
  // Dialog states
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [systemDialogOpen, setSystemDialogOpen] = useState(false);
  
  // Form states
  const [newPlatform, setNewPlatform] = useState({ name: "", color: "#A1A1AA", scoreAbbr: "" });
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [newSystem, setNewSystem] = useState({ platformId: "", name: "" });
  const [addSystemPlatformId, setAddSystemPlatformId] = useState(null);
  const [newSystemName, setNewSystemName] = useState("");
  // Inline editing for the user's own custom platforms/systems
  const [editCustomPlatform, setEditCustomPlatform] = useState({ id: null, name: "" });
  const [editCustomSystem, setEditCustomSystem] = useState({ id: null, name: "" });
  // Bulk reassign games off a custom platform before removing it
  const [reassign, setReassign] = useState({ open: false, platformId: null, platformName: "", count: 0, targetPlatformId: "", targetSystem: "", submitting: false, loadingUsage: true });
  
  // Clear database states
  const [clearStep, setClearStep] = useState(0);
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearChecks, setClearChecks] = useState({
    understand: false,
    noBackup: false,
    permanent: false,
  });
  const [clearing, setClearing] = useState(false);
  
  // Library maintenance state
  const [maintenanceData, setMaintenanceData] = useState(null);
  
  // Timezone state
  const [userTimezone, setUserTimezone] = useState("UTC");
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  
  // Active settings tab (persisted in URL ?tab=)
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return ["general", "library", "linked", "custom", "tags"].includes(t) ? t : "general";
    } catch {
      return "general";
    }
  });
  const changeTab = (t) => {
    setActiveTab(t);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", t);
      window.history.replaceState({}, "", url);
    } catch { /* ignore */ }
  };
  
  // User preferences state
  const [defaultAchievementSort, setDefaultAchievementSort] = useState("name_asc");
  // Secret-achievement preferences
  const [hideSecretAchievements, setHideSecretAchievements] = useState(true);
  const [hideSecretImage, setHideSecretImage] = useState(true);
  const [hideSecretName, setHideSecretName] = useState(true);
  const [autohideCompletedGames, setAutohideCompletedGames] = useState(false);
  const [hideBacklogGames, setHideBacklogGames] = useState(false);
  const [showHiddenGames, setShowHiddenGames] = useState(false);
  const [gamesPerPage, setGamesPerPage] = useState(50);
  const [dlcStatsMode, setDlcStatsMode] = useState("all");
  const [sessionTimeHours, setSessionTimeHours] = useState(2);
  const [libraryMaintenanceMode, setLibraryMaintenanceMode] = useState(false);
  const [showAchievementFacts, setShowAchievementFacts] = useState(true);
  const [newsletterOptOut, setNewsletterOptOut] = useState(false);
  const [multiplayerReminders, setMultiplayerReminders] = useState(true);
  const [collapseLinkedVersions, setCollapseLinkedVersions] = useState(true);
  const [hiddenPages, setHiddenPages] = useState([]);
  const [achievementViewMode, setAchievementViewMode] = useState("grid");
  const [includeXboxChallenges, setIncludeXboxChallenges] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  
  // Collapsible platform sections state
  const [expandedPlatforms, setExpandedPlatforms] = useState({});

  useEffect(() => {
    fetchData();
    fetchXboxStatus();
    fetchXboxOAuthStatus();
    fetchSteamStatus();
    fetchPsnStatus();
    fetchMaintenanceData();
    fetchTimezone();
    fetchPreferences();
    
    // Check for OAuth callback success/error
    const params = new URLSearchParams(window.location.search);
    if (params.get("xbox_auth_success")) {
      toast.success(`Xbox account connected: ${params.get("gamertag")}`);
      window.history.replaceState({}, "", "/settings");
      fetchXboxOAuthStatus();
    } else if (params.get("xbox_auth_error")) {
      const err = params.get("xbox_auth_error");
      const detail = params.get("detail");
      toast.error(`Xbox auth failed: ${err}${detail ? ` — ${detail}` : ""}`, { duration: 12000 });
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  const fetchTimezone = async () => {
    try {
      const response = await axios.get(`${API}/settings/timezone`);
      setUserTimezone(response.data.timezone || "UTC");
    } catch (error) {
      console.error("Failed to fetch timezone:", error);
    }
  };

  const handleTimezoneChange = async (newTimezone) => {
    setTimezoneLoading(true);
    try {
      await axios.put(`${API}/settings/timezone`, { timezone: newTimezone });
      setUserTimezone(newTimezone);
      toast.success(`Timezone updated to ${newTimezone}`);
    } catch (error) {
      console.error("Failed to update timezone:", error);
      toast.error("Failed to update timezone");
    } finally {
      setTimezoneLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/settings/preferences`);
      setDefaultAchievementSort(response.data.defaultAchievementSort || "name_asc");
      // Secret achievement prefs default to ON if backend doesn't return them
      setHideSecretAchievements(response.data.hideSecretAchievements !== false);
      setHideSecretImage(response.data.hideSecretImage !== false);
      setHideSecretName(response.data.hideSecretName !== false);
      setAutohideCompletedGames(response.data.autohideCompletedGames || false);
      setHideBacklogGames(response.data.hideBacklogGames || false);
      setShowHiddenGames(response.data.showHiddenGames || false);
      setGamesPerPage(response.data.gamesPerPage || 50);
      setDlcStatsMode(response.data.dlcStatsMode || "all");
      setSessionTimeHours(response.data.sessionTimeHours || 2);
      setLibraryMaintenanceMode(response.data.libraryMaintenanceMode || false);
      setShowAchievementFacts(response.data.showAchievementFacts !== false); // default true
      setNewsletterOptOut(response.data.newsletterOptOut || false);
      setMultiplayerReminders(response.data.multiplayerAvailabilityReminders !== false); // default true
      setCollapseLinkedVersions(response.data.collapseLinkedVersions !== false); // default true
      setHiddenPages(response.data.hiddenPages || []);
      setAchievementViewMode(response.data.achievementViewMode || "grid");
      setIncludeXboxChallenges(response.data.includeXboxChallenges || false);
    } catch (error) {
      console.error("Failed to fetch preferences:", error);
    }
  };

  const handleAchievementViewModeChange = async (newMode) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { achievementViewMode: newMode });
      setAchievementViewMode(newMode);
      toast.success(`Achievement view changed to ${newMode}`);
    } catch (error) {
      console.error("Failed to update view mode:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleDefaultSortChange = async (newSort) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { defaultAchievementSort: newSort });
      setDefaultAchievementSort(newSort);
      toast.success("Default achievement sorting updated");
    } catch (error) {
      console.error("Failed to update sort preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleAutohideChange = async (checked) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { autohideCompletedGames: checked });
      setAutohideCompletedGames(checked);
      toast.success(checked ? "Completed games will be hidden from library" : "All games will be shown in library");
    } catch (error) {
      console.error("Failed to update autohide preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleHideBacklogChange = async (checked) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { hideBacklogGames: checked });
      setHideBacklogGames(checked);
      toast.success(checked ? "Backlog games will be hidden from library" : "Backlog games will be shown in library");
    } catch (error) {
      console.error("Failed to update backlog preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleShowHiddenGamesChange = async (checked) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { showHiddenGames: checked });
      setShowHiddenGames(checked);
      toast.success(checked ? "Hidden games will be shown in library" : "Hidden games will be hidden from library");
    } catch (error) {
      console.error("Failed to update hidden games preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleUnhideAllGames = async () => {
    if (!confirm("Are you sure you want to unhide all hidden games?")) return;
    
    setPreferencesLoading(true);
    try {
      const response = await axios.put(`${API}/settings/preferences`, { unhideAllGames: true });
      const count = response.data.updated?.unhiddenCount || 0;
      toast.success(`Unhid ${count} game${count !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error("Failed to unhide all games:", error);
      toast.error("Failed to unhide games");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleGamesPerPageChange = async (value) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { gamesPerPage: parseInt(value) });
      setGamesPerPage(parseInt(value));
      toast.success(`Games per page set to ${value}`);
    } catch (error) {
      console.error("Failed to update games per page:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleDlcStatsModeChange = async (value) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { dlcStatsMode: value });
      setDlcStatsMode(value);
      toast.success("DLC stats display mode updated");
    } catch (error) {
      console.error("Failed to update DLC stats mode:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleSessionTimeChange = async (value) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { sessionTimeHours: parseFloat(value) });
      setSessionTimeHours(parseFloat(value));
      toast.success("Gaming session time updated");
    } catch (error) {
      console.error("Failed to update session time:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleMaintenanceModeChange = async (checked) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { libraryMaintenanceMode: checked });
      setLibraryMaintenanceMode(checked);
      toast.success(checked ? "Maintenance mode enabled - healthy games hidden" : "Maintenance mode disabled");
    } catch (error) {
      console.error("Failed to update maintenance mode:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleShowAchievementFactsChange = async (checked) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { showAchievementFacts: checked });
      setShowAchievementFacts(checked);
      toast.success(checked ? "Achievement facts & trivia enabled" : "Achievement facts & trivia hidden");
    } catch (error) {
      console.error("Failed to update achievement facts preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  // Multiplayer availability reminder nudge toggle
  const handleMultiplayerRemindersChange = async (enabled) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { multiplayerAvailabilityReminders: enabled });
      setMultiplayerReminders(enabled);
      toast.success(enabled ? "Availability reminders on" : "Availability reminders off");
    } catch (error) {
      console.error("Failed to update multiplayer reminder preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  // Collapse linked game versions in Browse
  const handleCollapseVersionsChange = async (enabled) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { collapseLinkedVersions: enabled });
      setCollapseLinkedVersions(enabled);
      toast.success(enabled ? "Linked versions will collapse in Browse" : "All versions will show in Browse");
    } catch (error) {
      console.error("Failed to update collapse-versions preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  // Newsletter toggle: switch ON = subscribed, so opt_out is the inverse.
  const handleNewsletterChange = async (subscribed) => {
    setPreferencesLoading(true);
    try {
      await axios.put(`${API}/settings/preferences`, { newsletterOptOut: !subscribed });
      setNewsletterOptOut(!subscribed);
      toast.success(subscribed ? "Subscribed to newsletter emails" : "Unsubscribed from newsletter emails");
    } catch (error) {
      console.error("Failed to update newsletter preference:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleHiddenPageToggle = async (pageKey) => {
    setPreferencesLoading(true);
    const newHiddenPages = hiddenPages.includes(pageKey)
      ? hiddenPages.filter(p => p !== pageKey)
      : [...hiddenPages, pageKey];
    
    try {
      await axios.put(`${API}/settings/preferences`, { hiddenPages: newHiddenPages });
      setHiddenPages(newHiddenPages);
      toast.success("Menu visibility updated");
    } catch (error) {
      console.error("Failed to update hidden pages:", error);
      toast.error("Failed to update preference");
    } finally {
      setPreferencesLoading(false);
    }
  };

  const togglePlatformExpanded = (platformId) => {
    setExpandedPlatforms(prev => ({
      ...prev,
      [platformId]: prev[platformId] === false ? true : false
    }));
  };

  const fetchMaintenanceData = async () => {
    setMaintenanceLoading(true);
    try {
      const response = await axios.get(`${API}/library/maintenance`);
      setMaintenanceData(response.data);
    } catch (error) {
      console.error("Failed to fetch maintenance data:", error);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const fetchXboxOAuthStatus = async () => {
    try {
      const response = await axios.get(`${API}/xbox/oauth/status`);
      setXboxOAuthStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Xbox OAuth status:", error);
    }
  };

  const handleXboxOAuthSignIn = async () => {
    setXboxOAuthLoading(true);
    try {
      const response = await axios.get(`${API}/xbox/oauth/url`);
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Failed to get Xbox OAuth URL:", error);
      toast.error("Failed to start Xbox sign in");
      setXboxOAuthLoading(false);
    }
  };

  const handleXboxOAuthDisconnect = async () => {
    try {
      await axios.delete(`${API}/xbox/oauth/disconnect`);
      setXboxOAuthStatus({ authenticated: false, gamertag: null });
      toast.success("Xbox account disconnected");
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleXboxOAuthSync = async () => {
    await executeXboxOAuthSync();
  };
  
  const executeXboxOAuthSync = async () => {
    setXboxSyncing(true);
    setXboxSyncResult(null);
    
    try {
      const response = await axios.post(`${API}/xbox/oauth/sync-profile`);
      setXboxSyncResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Xbox OAuth sync error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to sync Xbox profile";
      toast.error(errorMsg);
      setXboxSyncResult({ success: false, message: errorMsg });
    } finally {
      setXboxSyncing(false);
    }
  };

  const fetchPsnStatus = async () => {
    try {
      const response = await axios.get(`${API}/playstation/status`);
      setPsnStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch PlayStation status:", error);
    }
  };

  const handlePsnProfileSync = async () => {
    await executePsnProfileSync();
  };
  
  const executePsnProfileSync = async () => {
    setPsnSyncing(true);
    setPsnSyncResult(null);
    
    try {
      const response = await axios.post(`${API}/playstation/sync-profile`);
      setPsnSyncResult(response.data);
      toast.success(`Synced ${response.data.gamesImported} games and ${response.data.trophiesImported} trophies!`);
    } catch (error) {
      console.error("PlayStation sync error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to sync PlayStation profile";
      if (errorMsg.toLowerCase().includes("expired")) {
        toast.error("NPSSO token expired. Please get a fresh token.", { duration: 8000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setPsnSyncing(false);
    }
  };

  const handleSaveNpsso = async () => {
    if (!npssoInput.trim()) {
      toast.error("Please enter your NPSSO token");
      return;
    }
    
    setPsnSaving(true);
    try {
      const response = await axios.post(`${API}/playstation/save-token`, {
        npsso_token: npssoInput.trim()
      });
      setPsnStatus({
        configured: true,
        onlineId: response.data.onlineId,
        accountId: response.data.accountId,
      });
      setNpssoInput("");
      toast.success(`PlayStation account "${response.data.onlineId}" connected!`);
    } catch (error) {
      console.error("PlayStation token error:", error);
      toast.error(error.response?.data?.detail || "Failed to save token");
    } finally {
      setPsnSaving(false);
    }
  };

  const handleClearNpsso = async () => {
    setPsnClearing(true);
    try {
      await axios.post(`${API}/playstation/clear-token`);
      setPsnStatus({ configured: false, onlineId: null });
      setNpssoInput("");
      toast.success("PlayStation token cleared");
    } catch (error) {
      toast.error("Failed to clear token");
    } finally {
      setPsnClearing(false);
    }
  };

  const fetchSteamStatus = async () => {
    try {
      const response = await axios.get(`${API}/steam/status`);
      setSteamStatus(response.data);
      if (response.data.steamId) {
        setSteamIdInput(response.data.steamId);
      }
    } catch (error) {
      console.error("Failed to fetch Steam status:", error);
    }
  };

  const handleSteamProfileSync = async () => {
    await executeSteamProfileSync();
  };
  
  const executeSteamProfileSync = async () => {
    setSteamSyncing(true);
    setSteamSyncResult(null);
    
    try {
      const response = await axios.post(`${API}/steam/sync-profile`);
      setSteamSyncResult(response.data);
      toast.success(`Synced ${response.data.gamesImported} games and ${response.data.achievementsImported} achievements!`);
    } catch (error) {
      console.error("Steam sync error:", error);
      toast.error(error.response?.data?.detail || "Failed to sync Steam profile");
    } finally {
      setSteamSyncing(false);
    }
  };

  const handleSaveSteamId = async () => {
    if (!steamIdInput.trim()) {
      toast.error("Please enter a Steam ID");
      return;
    }
    
    setSteamSaving(true);
    try {
      const response = await axios.post(`${API}/steam/save-steam-id`, {
        steamId: steamIdInput.trim()
      });
      setSteamStatus({
        configured: true,
        steamId: response.data.steamId,
        steamName: response.data.steamName,
      });
      toast.success(`Steam ID saved! Welcome, ${response.data.steamName}`);
    } catch (error) {
      console.error("Steam ID error:", error);
      toast.error(error.response?.data?.detail || "Failed to save Steam ID");
    } finally {
      setSteamSaving(false);
    }
  };

  const handleClearSteamId = async () => {
    setSteamClearing(true);
    try {
      await axios.post(`${API}/steam/clear-steam-id`);
      setSteamStatus({ configured: false, steamId: null, steamName: null });
      setSteamIdInput("");
      toast.success("Steam ID cleared");
    } catch (error) {
      toast.error("Failed to clear Steam ID");
    } finally {
      setSteamClearing(false);
    }
  };

  const fetchXboxStatus = async () => {
    try {
      const response = await axios.get(`${API}/xbox/status`);
      setXboxStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Xbox status:", error);
    }
  };

  const handleXboxProfileSync = async () => {
    setXboxSyncing(true);
    setXboxSyncResult(null);
    
    try {
      const response = await axios.post(`${API}/xbox/sync-profile`);
      setXboxSyncResult(response.data);
      toast.success(`Synced ${response.data.gamesImported} games and ${response.data.achievementsImported} achievements!`);
    } catch (error) {
      console.error("Xbox sync error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to sync Xbox profile";
      const statusCode = error.response?.status;
      
      if (statusCode === 429 || errorMsg.toLowerCase().includes("rate limit")) {
        toast.error("OpenXBL rate limit exceeded (150 req/hour). Please wait 30-60 minutes.", {
          duration: 8000,
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setXboxSyncing(false);
    }
  };

  const handleCleanupDuplicates = async () => {
    setCleaningDuplicates(true);
    try {
      const response = await axios.post(`${API}/xbox/cleanup-duplicates`);
      const result = response.data;
      if (result.duplicates_deleted > 0) {
        toast.success(`Cleaned up ${result.duplicates_deleted} duplicate games!`);
      } else {
        toast.info("No duplicate games found.");
      }
    } catch (error) {
      console.error("Cleanup error:", error);
      toast.error(error.response?.data?.detail || "Failed to clean up duplicates");
    } finally {
      setCleaningDuplicates(false);
    }
  };

  const fetchData = async () => {
    try {
      const [tagsRes, platformsRes, systemsRes, usageRes, gameTagsRes, gameTagsUsageRes] = await Promise.all([
        axios.get(`${API}/tags`),
        axios.get(`${API}/platforms`),
        axios.get(`${API}/systems`),
        axios.get(`${API}/tags/usage`),
        axios.get(`${API}/game-tags`),
        // Backend aggregation replaces a 1.76 MB `/games` payload that was
        // being downloaded solely to count game-tag usage on the client.
        axios.get(`${API}/game-tags/usage`).catch(() => ({ data: {} })),
      ]);
      setTags(tagsRes.data);
      setPlatforms(platformsRes.data);
      setSystems(systemsRes.data);
      setTagUsage(usageRes.data);
      setGameTags(gameTagsRes.data);
      setGameTagUsage(gameTagsUsageRes.data || {});
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Game Tag handlers
  const handleCreateGameTag = async () => {
    if (!newGameTag.name) {
      toast.error("Please enter a tag name");
      return;
    }
    try {
      await axios.post(`${API}/game-tags`, newGameTag);
      toast.success("Game tag created!");
      setGameTagDialogOpen(false);
      setNewGameTag({ name: "", color: "#6366F1" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create game tag");
    }
  };

  const handleDeleteGameTag = async (tagId, tagName) => {
    try {
      await axios.delete(`${API}/game-tags/${tagId}`);
      toast.success(`Game tag "${tagName}" deleted`);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete game tag");
    }
  };

  // Platform handlers
  const handleCreatePlatform = async () => {
    if (!newPlatform.name) {
      toast.error("Please enter a platform name");
      return;
    }
    try {
      await axios.post(`${API}/platforms`, newPlatform);
      toast.success("Platform created!");
      setPlatformDialogOpen(false);
      setNewPlatform({ name: "", color: "#A1A1AA", scoreAbbr: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create platform");
    }
  };

  const handleTogglePlatform = async (platformId, currentEnabled) => {
    try {
      await axios.put(`${API}/platforms/${platformId}/toggle?enabled=${!currentEnabled}`);
      fetchData();
      toast.success(`Platform ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle platform");
    }
  };

  const handleToggleIncludeZeroUnlocks = async (platformId, currentValue) => {
    try {
      await axios.put(`${API}/platforms/${platformId}/include-zero-unlocks?include=${!currentValue}`);
      fetchData();
      toast.success(`Unstarted games ${!currentValue ? 'included' : 'excluded'} from stats`);
    } catch (error) {
      toast.error("Failed to update setting");
    }
  };

  const handleToggleIncludeZeroUnlocksDlc = async (platformId, currentValue) => {
    try {
      await axios.put(`${API}/platforms/${platformId}/include-zero-unlocks-dlc?include=${!currentValue}`);
      fetchData();
      toast.success(`Unstarted DLCs ${!currentValue ? 'included' : 'excluded'} from stats`);
    } catch (error) {
      toast.error("Failed to update setting");
    }
  };

  const handleUpdatePlatform = async () => {
    if (!editingPlatform) return;
    try {
      await axios.put(`${API}/platforms/${editingPlatform.id}`, {
        name: editingPlatform.name,
        color: editingPlatform.color,
        scoreAbbr: editingPlatform.scoreAbbr || "",
      });
      toast.success("Platform updated!");
      setEditingPlatform(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update platform");
    }
  };

  // System handlers
  const handleCreateSystem = async () => {
    if (!newSystem.platformId || !newSystem.name) {
      toast.error("Please select a platform and enter a system name");
      return;
    }
    try {
      await axios.post(`${API}/systems`, newSystem);
      toast.success("System created!");
      setSystemDialogOpen(false);
      setNewSystem({ platformId: "", name: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create system");
    }
  };

  // Create system for a specific platform (inline in platform section)
  const handleCreateSystemForPlatform = async (platformId) => {
    if (!newSystemName.trim()) {
      toast.error("Please enter a system name");
      return;
    }
    try {
      await axios.post(`${API}/systems`, { platformId, name: newSystemName.trim() });
      toast.success("System created!");
      setAddSystemPlatformId(null);
      setNewSystemName("");
      fetchData();
    } catch (error) {
      toast.error("Failed to create system");
    }
  };

  const handleDeleteSystem = async (systemId, systemName) => {
    try {
      await axios.delete(`${API}/systems/${systemId}`);
      toast.success(`System "${systemName}" deleted`);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete system");
    }
  };

  // ===== User-owned custom platform/system management =====
  const handleRenameCustomPlatform = async () => {
    const name = editCustomPlatform.name.trim();
    if (!name) return;
    try {
      await axios.put(`${API}/platforms/custom/${editCustomPlatform.id}`, { name });
      toast.success("Platform renamed");
      setEditCustomPlatform({ id: null, name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename platform");
    }
  };

  const handleDeleteCustomPlatform = async (platformId, platformName) => {
    try {
      await axios.delete(`${API}/platforms/custom/${platformId}`);
      toast.success(`Platform "${platformName}" removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove platform");
    }
  };

  const handleRenameCustomSystem = async () => {
    const name = editCustomSystem.name.trim();
    if (!name) return;
    try {
      await axios.put(`${API}/systems/custom/${editCustomSystem.id}`, { name });
      toast.success("System renamed");
      setEditCustomSystem({ id: null, name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename system");
    }
  };

  const handleDeleteCustomSystem = async (systemId, systemName) => {
    try {
      await axios.delete(`${API}/systems/custom/${systemId}`);
      toast.success(`System "${systemName}" removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove system");
    }
  };

  const openReassignDialog = async (platformId, platformName) => {
    setReassign({ open: true, platformId, platformName, count: 0, targetPlatformId: "", targetSystem: "", submitting: false, loadingUsage: true });
    try {
      const { data } = await axios.get(`${API}/platforms/custom/${platformId}/usage`);
      setReassign((r) => ({ ...r, count: data.gameCount || 0, loadingUsage: false }));
    } catch (error) {
      setReassign((r) => ({ ...r, loadingUsage: false }));
    }
  };

  const handleReassignAndRemove = async () => {
    if (!reassign.targetPlatformId) {
      toast.error("Please choose a destination platform");
      return;
    }
    setReassign((r) => ({ ...r, submitting: true }));
    try {
      const { data } = await axios.post(`${API}/platforms/custom/${reassign.platformId}/reassign`, {
        targetPlatformId: reassign.targetPlatformId,
        targetSystem: reassign.targetSystem,
        deleteAfter: true,
      });
      toast.success(`Moved ${data.moved} game${data.moved === 1 ? "" : "s"} and removed "${reassign.platformName}"`);
      setReassign({ open: false, platformId: null, platformName: "", count: 0, targetPlatformId: "", targetSystem: "", submitting: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reassign games");
      setReassign((r) => ({ ...r, submitting: false }));
    }
  };

  // Clear database handler
  const handleClearDatabase = async () => {
    if (clearConfirmText !== "DELETE ALL DATA") {
      toast.error("Please type the confirmation text exactly");
      return;
    }
    setClearing(true);
    try {
      await axios.delete(`${API}/admin/clear-database?confirmation=CONFIRM_DELETE_ALL_DATA`);
      toast.success("Database cleared successfully");
      setClearStep(0);
      setClearConfirmText("");
      setClearChecks({ understand: false, noBackup: false, permanent: false });
      fetchData();
    } catch (error) {
      toast.error("Failed to clear database");
    } finally {
      setClearing(false);
    }
  };

  // Helper to render platform settings (toggle + systems) for each platform section
  const renderPlatformSettings = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    // Return null if platform not found (data not loaded yet or doesn't exist)
    if (!platform) {
      return (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center justify-center py-4 text-white/40">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading platform settings...
          </div>
        </div>
      );
    }
    
    const platformSystems = systems[platformId] || [];
    
    return (
      <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
        {/* Platform Toggle */}
        <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Power className="w-4 h-4 text-white/50" />
            <span className="text-white/70 text-sm">Platform Enabled</span>
          </div>
          <Switch
            checked={platform.isEnabled !== false}
            onCheckedChange={() => handleTogglePlatform(platform.id, platform.isEnabled !== false)}
            data-testid={`${platformId}-toggle`}
          />
        </div>

        {/* Statistics Settings */}
        <div className="space-y-2 p-3 bg-black/30 rounded-lg">
          <span className="text-white/50 text-xs uppercase tracking-wider">Statistics Settings</span>
          
          {/* Include unstarted games in stats */}
          <div className="flex items-center justify-between py-1">
            <div className="flex-1">
              <span className="text-white/70 text-sm">Include unstarted games</span>
              <p className="text-white/40 text-xs">Count games with 0 unlocks in completion %</p>
            </div>
            <Switch
              checked={platform.includeZeroUnlocks || false}
              onCheckedChange={() => handleToggleIncludeZeroUnlocks(platform.id, platform.includeZeroUnlocks || false)}
              data-testid={`${platformId}-include-zero-unlocks`}
            />
          </div>
          
          {/* Include unstarted DLCs in stats */}
          <div className="flex items-center justify-between py-1">
            <div className="flex-1">
              <span className="text-white/70 text-sm">Include unstarted DLCs</span>
              <p className="text-white/40 text-xs">Count DLCs with 0 unlocks in completion %</p>
            </div>
            <Switch
              checked={platform.includeZeroUnlocksDlc || false}
              onCheckedChange={() => handleToggleIncludeZeroUnlocksDlc(platform.id, platform.includeZeroUnlocksDlc || false)}
              data-testid={`${platformId}-include-zero-unlocks-dlc`}
            />
          </div>
        </div>
        
        {/* Systems Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-sm font-medium flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Systems
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAddSystemPlatformId(addSystemPlatformId === platformId ? null : platformId);
                setNewSystemName("");
              }}
              className="text-white/50 hover:text-white h-7 px-2"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add
            </Button>
          </div>
          
          {/* Add System Input (shown inline) */}
          {addSystemPlatformId === platformId && (
            <div className="flex gap-2 animate-in slide-in-from-top-2">
              <Input
                placeholder="e.g., Xbox Series X, PS5"
                value={newSystemName}
                onChange={(e) => setNewSystemName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateSystemForPlatform(platformId)}
                className="bg-black/50 border-white/10 h-8 text-sm flex-1"
                autoFocus
              />
              <Button
                size="sm"
                onClick={() => handleCreateSystemForPlatform(platformId)}
                disabled={!newSystemName.trim()}
                className="h-8 bg-white/10 hover:bg-white/20"
              >
                Add
              </Button>
            </div>
          )}
          
          {/* Systems List */}
          <div className="flex flex-wrap gap-2">
            {platformSystems.length === 0 ? (
              <span className="text-white/30 text-xs">No systems added yet</span>
            ) : (
              platformSystems.map((system) => (
                <div
                  key={system.id}
                  className="flex items-center gap-1.5 px-2 py-1 bg-black/30 rounded border border-white/10 group"
                >
                  <span className="text-white text-xs">{system.name}</span>
                  <button
                    onClick={() => handleDeleteSystem(system.id, system.name)}
                    className="text-white/20 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  // Sort tags alphabetically and filter out "Incomplete Sync"
  const sortedTags = [...tags]
    .filter((tag) => tag.name?.toLowerCase() !== "incomplete sync")
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-6 pb-20">
      <div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
          Settings
        </h1>
        <p className="text-white/50 mt-1">Manage platforms, systems, and tags</p>
      </div>

      <Tabs value={activeTab} onValueChange={changeTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-white/5 border border-white/10 p-1 mb-6">
          <TabsTrigger value="general" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="settings-tab-general">General</TabsTrigger>
          <TabsTrigger value="library" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="settings-tab-library">Library</TabsTrigger>
          <TabsTrigger value="linked" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="settings-tab-linked">Linked Platforms</TabsTrigger>
          <TabsTrigger value="custom" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="settings-tab-custom">Custom Platforms</TabsTrigger>
          <TabsTrigger value="tags" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="settings-tab-tags">Tags</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6 mt-0">
      {/* Timezone Settings */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Timezone
          </CardTitle>
          <CardDescription className="text-white/50">
            Set your timezone for accurate streak calculations and achievement timestamps
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select value={userTimezone} onValueChange={handleTimezoneChange} disabled={timezoneLoading}>
              <SelectTrigger className="w-[280px] bg-[#121212] border-white/10 text-white">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 max-h-[300px]">
                <SelectItem value="UTC">UTC (Coordinated Universal Time)</SelectItem>
                <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                <SelectItem value="Pacific/Honolulu">Hawaii Time (HT)</SelectItem>
                <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                <SelectItem value="Europe/Paris">Central European (CET)</SelectItem>
                <SelectItem value="Europe/Moscow">Moscow (MSK)</SelectItem>
                <SelectItem value="Asia/Tokyo">Japan (JST)</SelectItem>
                <SelectItem value="Asia/Shanghai">China (CST)</SelectItem>
                <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                <SelectItem value="Australia/Sydney">Sydney (AEST)</SelectItem>
                <SelectItem value="Australia/Perth">Perth (AWST)</SelectItem>
                <SelectItem value="Pacific/Auckland">New Zealand (NZST)</SelectItem>
              </SelectContent>
            </Select>
            {timezoneLoading && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
          </div>
          <p className="text-white/30 text-xs mt-2">
            This affects when achievements count toward daily streaks (midnight in your timezone)
          </p>
        </CardContent>
      </Card>

      {/* Community & Content */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Community &amp; Content
          </CardTitle>
          <CardDescription className="text-white/50">
            Control community emails and achievement trivia
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Show Achievement Facts */}
          <div className="flex items-start justify-between p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <div className="flex-1">
              <label className="text-white text-sm font-medium block flex items-center gap-2">
                <span className="text-lg">💡</span>
                Achievement Facts & Trivia
              </label>
              <p className="text-white/30 text-xs mt-1">
                Show community-contributed facts and trivia for achievements. You can also submit your own!
              </p>
            </div>
            <Switch
              checked={showAchievementFacts}
              onCheckedChange={handleShowAchievementFactsChange}
              disabled={preferencesLoading}
              className="data-[state=checked]:bg-amber-600"
            />
          </div>

          {/* Newsletter Emails */}
          <div className="flex items-start justify-between p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg" data-testid="newsletter-setting-row">
            <div className="flex-1 pr-3">
              <label className="text-white text-sm font-medium block flex items-center gap-2">
                <Mail className="w-4 h-4 text-purple-400" />
                Community Newsletter Emails
              </label>
              <p className="text-white/30 text-xs mt-1">
                Get an email whenever a new community news post goes live. Turn this off to stop receiving them.
              </p>
            </div>
            <Switch
              data-testid="newsletter-toggle"
              checked={!newsletterOptOut}
              onCheckedChange={handleNewsletterChange}
              disabled={preferencesLoading}
              className="data-[state=checked]:bg-purple-600"
            />
          </div>

          {/* Multiplayer Availability Reminders */}
          <div className="flex items-start justify-between p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg" data-testid="mp-reminders-setting-row">
            <div className="flex-1 pr-3">
              <label className="text-white text-sm font-medium block flex items-center gap-2">
                <span className="text-lg">🎮</span>
                Multiplayer Availability Reminders
              </label>
              <p className="text-white/30 text-xs mt-1">
                Nudge me to set a multiplayer availability schedule so other players can match with me. Turn this off if you don't play multiplayer.
              </p>
            </div>
            <Switch
              data-testid="mp-reminders-toggle"
              checked={multiplayerReminders}
              onCheckedChange={handleMultiplayerRemindersChange}
              disabled={preferencesLoading}
              className="data-[state=checked]:bg-cyan-600"
            />
          </div>

          {/* Collapse Linked Game Versions in Browse */}
          <div className="flex items-start justify-between p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg" data-testid="collapse-versions-setting-row">
            <div className="flex-1 pr-3">
              <label className="text-white text-sm font-medium block flex items-center gap-2">
                <Layers className="w-4 h-4 text-cyan-400" />
                Collapse Linked Game Versions
              </label>
              <p className="text-white/30 text-xs mt-1">
                Show different versions of the same game as a single Browse tile. Click it to open a hub listing every version and which ones are in your library. Turn off to see every version separately.
              </p>
            </div>
            <Switch
              data-testid="collapse-versions-toggle"
              checked={collapseLinkedVersions}
              onCheckedChange={handleCollapseVersionsChange}
              disabled={preferencesLoading}
              className="data-[state=checked]:bg-cyan-600"
            />
          </div>
        </CardContent>
      </Card>

        </TabsContent>
        <TabsContent value="library" className="space-y-6 mt-0">
      {/* Library Preferences */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Gamepad2 className="w-5 h-5" />
            Library Preferences
          </CardTitle>
          <CardDescription className="text-white/50">
            Customize how games and achievements are displayed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Games Per Page */}
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              Games Per Page
            </label>
            <div className="flex items-center gap-4">
              <Select value={gamesPerPage.toString()} onValueChange={handleGamesPerPageChange} disabled={preferencesLoading}>
                <SelectTrigger className="w-[180px] bg-[#121212] border-white/10 text-white">
                  <SelectValue placeholder="Select page size" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="20">20 games</SelectItem>
                  <SelectItem value="50">50 games</SelectItem>
                  <SelectItem value="100">100 games</SelectItem>
                </SelectContent>
              </Select>
              {preferencesLoading && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
            </div>
            <p className="text-white/30 text-xs mt-2">
              Maximum number of games shown per page in the library
            </p>
          </div>

          {/* Achievement View Mode */}
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              Achievement Display Mode
            </label>
            <div className="flex items-center gap-4">
              <Select value={achievementViewMode} onValueChange={handleAchievementViewModeChange} disabled={preferencesLoading}>
                <SelectTrigger className="w-[280px] bg-[#121212] border-white/10 text-white">
                  <SelectValue placeholder="Select view mode" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="grid">Grid View</SelectItem>
                  <SelectItem value="list">List View</SelectItem>
                </SelectContent>
              </Select>
              {preferencesLoading && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
            </div>
            <p className="text-white/30 text-xs mt-2">
              Choose between grid or compact list view for achievements on game pages
            </p>
          </div>

          {/* Default Achievement Sorting */}
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              Default Achievement Sorting
            </label>
            <div className="flex items-center gap-4">
              <Select value={defaultAchievementSort} onValueChange={handleDefaultSortChange} disabled={preferencesLoading}>
                <SelectTrigger className="w-[280px] bg-[#121212] border-white/10 text-white">
                  <SelectValue placeholder="Select default sort" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                  <SelectItem value="score_desc">Score (High to Low)</SelectItem>
                  <SelectItem value="score_asc">Score (Low to High)</SelectItem>
                  <SelectItem value="unlocked_first">Unlocked First</SelectItem>
                  <SelectItem value="locked_first">Locked First</SelectItem>
                  <SelectItem value="date_desc">Date Unlocked (Newest)</SelectItem>
                  <SelectItem value="date_asc">Date Unlocked (Oldest)</SelectItem>
                  <SelectItem value="rarity_asc">Rarity (Rarest First)</SelectItem>
                  <SelectItem value="rarity_desc">Rarity (Most Common)</SelectItem>
                </SelectContent>
              </Select>
              {preferencesLoading && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
            </div>
            <p className="text-white/30 text-xs mt-2">
              Sets the initial sort order when viewing achievements on game pages
            </p>
          </div>

          {/* Secret Achievements */}
          <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <label className="text-white text-sm font-medium" htmlFor="hide-secret-achievements">
                  Hide secret achievements until unlocked
                </label>
                <p className="text-white/50 text-xs mt-1">
                  When on, achievements flagged Secret stay hidden on the game page until you unlock them. Mimics how PlayStation, Xbox and Steam handle them on their respective sites.
                </p>
              </div>
              <Switch
                id="hide-secret-achievements"
                data-testid="hide-secret-achievements-switch"
                checked={hideSecretAchievements}
                onCheckedChange={async (checked) => {
                  setHideSecretAchievements(checked);
                  try {
                    await axios.put(`${API}/settings/preferences`, { hideSecretAchievements: checked });
                  } catch {
                    setHideSecretAchievements(!checked);
                    toast.error("Failed to save preference");
                  }
                }}
              />
            </div>
            {/* Sub-toggles — only relevant when the master switch is on */}
            <div className={`pl-4 space-y-3 border-l border-amber-500/20 ${hideSecretAchievements ? "" : "opacity-40 pointer-events-none"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <label className="text-white/90 text-sm" htmlFor="hide-secret-image">
                    Also hide the achievement image
                  </label>
                  <p className="text-white/50 text-xs mt-1">
                    Replaces the icon with a lock placeholder. Turn off to keep the artwork visible.
                  </p>
                </div>
                <Switch
                  id="hide-secret-image"
                  data-testid="hide-secret-image-switch"
                  checked={hideSecretImage}
                  onCheckedChange={async (checked) => {
                    setHideSecretImage(checked);
                    try {
                      await axios.put(`${API}/settings/preferences`, { hideSecretImage: checked });
                    } catch {
                      setHideSecretImage(!checked);
                      toast.error("Failed to save preference");
                    }
                  }}
                />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <label className="text-white/90 text-sm" htmlFor="hide-secret-name">
                    Also hide the name and description
                  </label>
                  <p className="text-white/50 text-xs mt-1">
                    Replaces the name with "Secret Achievement". Turn off if you want to read the name but keep the artwork hidden.
                  </p>
                </div>
                <Switch
                  id="hide-secret-name"
                  data-testid="hide-secret-name-switch"
                  checked={hideSecretName}
                  onCheckedChange={async (checked) => {
                    setHideSecretName(checked);
                    try {
                      await axios.put(`${API}/settings/preferences`, { hideSecretName: checked });
                    } catch {
                      setHideSecretName(!checked);
                      toast.error("Failed to save preference");
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {/* DLC Stats Display Mode */}
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              DLC Stats Display
            </label>
            <div className="flex items-center gap-4">
              <Select value={dlcStatsMode} onValueChange={handleDlcStatsModeChange} disabled={preferencesLoading}>
                <SelectTrigger className="w-[280px] bg-[#121212] border-white/10 text-white">
                  <SelectValue placeholder="Select DLC display mode" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="all">All (Base Game + All DLCs)</SelectItem>
                  <SelectItem value="base_only">Base Game Only</SelectItem>
                  <SelectItem value="base_active">Base + Active DLCs</SelectItem>
                  <SelectItem value="active_only">Active DLCs Only</SelectItem>
                </SelectContent>
              </Select>
              {preferencesLoading && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
            </div>
            <p className="text-white/30 text-xs mt-2">
              Controls how DLC achievements are counted in library stats. &quot;Active&quot; means DLCs with at least 1 unlock.
            </p>
          </div>

          {/* Gaming Session Time */}
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              Gaming Session Length
            </label>
            <div className="flex items-center gap-4">
              <Select value={sessionTimeHours.toString()} onValueChange={handleSessionTimeChange} disabled={preferencesLoading}>
                <SelectTrigger className="w-[280px] bg-[#121212] border-white/10 text-white">
                  <SelectValue placeholder="Select session length" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="0.5">30 minutes</SelectItem>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="1.5">1.5 hours</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="2.5">2.5 hours</SelectItem>
                  <SelectItem value="3">3 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="5">5 hours</SelectItem>
                  <SelectItem value="6">6 hours</SelectItem>
                </SelectContent>
              </Select>
              {preferencesLoading && <Loader2 className="w-4 h-4 animate-spin text-white/50" />}
            </div>
            <p className="text-white/30 text-xs mt-2">
              Used to calculate estimated gaming sessions needed to complete a game based on its completion time estimate.
            </p>
          </div>

          {/* Autohide Completed Games */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label className="text-white text-sm font-medium block">
                Hide Completed Games
              </label>
              <p className="text-white/30 text-xs mt-1">
                Hide games with 100% completion from the library view. They&apos;ll still count in your stats.
              </p>
            </div>
            <Switch
              checked={autohideCompletedGames}
              onCheckedChange={handleAutohideChange}
              disabled={preferencesLoading}
              className="data-[state=checked]:bg-green-600"
            />
          </div>

          {/* Hide Backlog Games */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label className="text-white text-sm font-medium block">
                Hide Backlog Games
              </label>
              <p className="text-white/30 text-xs mt-1">
                Hide games tagged as &quot;Backlog&quot; (unstarted games you plan to play later). They&apos;ll still appear in search with a special tag.
              </p>
            </div>
            <Switch
              checked={hideBacklogGames}
              onCheckedChange={handleHideBacklogChange}
              disabled={preferencesLoading}
              className="data-[state=checked]:bg-amber-600"
            />
          </div>

          {/* Show Hidden Games */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label className="text-white text-sm font-medium block">
                Show Hidden Games
              </label>
              <p className="text-white/30 text-xs mt-1">
                Reveal games you&apos;ve hidden from the library. Hidden games still count toward your stats but won&apos;t clutter your main view.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnhideAllGames}
                disabled={preferencesLoading}
                className="text-xs h-8 border-gray-600 hover:bg-gray-600/20"
              >
                Unhide All
              </Button>
              <Switch
                checked={showHiddenGames}
                onCheckedChange={handleShowHiddenGamesChange}
                disabled={preferencesLoading}
                className="data-[state=checked]:bg-gray-600"
              />
            </div>
          </div>

          {/* Library Maintenance Mode */}
          <div className="flex items-start justify-between p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <div className="flex-1">
              <label className="text-white text-sm font-medium block flex items-center gap-2">
                <Wrench className="w-4 h-4 text-amber-500" />
                Maintenance Mode
              </label>
              <p className="text-white/30 text-xs mt-1">
                Temporarily hide &quot;healthy&quot; games to focus on items needing attention (incomplete sync, missing metadata, etc.)
              </p>
            </div>
            <Switch
              checked={libraryMaintenanceMode}
              onCheckedChange={handleMaintenanceModeChange}
              disabled={preferencesLoading}
              className="data-[state=checked]:bg-amber-600"
            />
          </div>
        </CardContent>
      </Card>

        </TabsContent>
        <TabsContent value="general" className="space-y-6 mt-0">
      {/* Menu Visibility Settings */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <ListChecks className="w-5 h-5" />
            Menu Visibility
          </CardTitle>
          <CardDescription className="text-white/50">
            Choose which pages appear in the navigation menu. Hidden pages are still accessible via direct URL.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { key: "dashboard", label: "Dashboard", icon: "📊" },
              { key: "priorities", label: "Priorities", icon: "⭐" },
              { key: "notifications", label: "Notifications", icon: "🔔" },
              { key: "achievements", label: "Achievements", icon: "🏆" },
              { key: "statistics", label: "Statistics", icon: "📈" },
              { key: "social", label: "Community Items", icon: "👥" },
              { key: "browse", label: "Browse", icon: "📖" },
              { key: "wishlist", label: "Wish List", icon: "🎁" },
            ].map(page => {
              const locked = page.key === "notifications" && !isAdmin;
              return (
              <div
                key={page.key}
                className={`flex flex-col p-3 rounded-lg border transition-all ${
                  locked
                    ? "bg-white/[0.02] border-white/5 opacity-70 cursor-not-allowed"
                    : hiddenPages.includes(page.key)
                    ? "bg-red-500/5 border-red-500/20 opacity-60 cursor-pointer"
                    : "bg-[#121212] border-white/5 hover:border-white/20 cursor-pointer"
                }`}
                onClick={() => { if (!locked) handleHiddenPageToggle(page.key); }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{page.icon}</span>
                    <span className="text-white text-sm">{page.label}</span>
                  </div>
                  <Switch
                    checked={locked ? false : !hiddenPages.includes(page.key)}
                    onCheckedChange={() => { if (!locked) handleHiddenPageToggle(page.key); }}
                    disabled={preferencesLoading || locked}
                    className="data-[state=checked]:bg-green-600"
                    data-testid={`visibility-toggle-${page.key}`}
                  />
                </div>
                {locked && (
                  <p className="text-amber-400/80 text-[11px] mt-2 leading-snug" data-testid="notifications-deactivated-note">
                    This feature is currently deactivated while testing continues.
                  </p>
                )}
              </div>
              );
            })}
          </div>
          <p className="text-white/30 text-xs mt-3">
            Note: Settings and Games pages cannot be hidden.
          </p>
        </CardContent>
      </Card>

        </TabsContent>
        <TabsContent value="library" className="space-y-6 mt-0">
      {/* Library Tools - Moved to top */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Library Tools
          </CardTitle>
          <CardDescription className="text-white/50">
            Quick overview of your library health and game status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Library Health Stats - Row 1: Game Status */}
          {maintenanceLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-white/50" />
            </div>
          ) : maintenanceData ? (
            <>
              {/* Row 1: Game Status Counts */}
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Star className="w-3 h-3 text-yellow-500" />
                    Priority
                  </div>
                  <p className="text-2xl font-bold text-yellow-400">{maintenanceData.summary?.priorityGames || 0}</p>
                </div>
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                    Completed
                  </div>
                  <p className="text-2xl font-bold text-green-400">{maintenanceData.summary?.completedGames || 0}</p>
                </div>
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Archive className="w-3 h-3 text-slate-500" />
                    Backlog
                  </div>
                  <p className="text-2xl font-bold text-slate-400">{maintenanceData.summary?.backlogGames || 0}</p>
                </div>
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5 hidden md:block">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <ListChecks className="w-3 h-3 text-green-500" />
                    Healthy
                  </div>
                  <p className="text-2xl font-bold text-green-400">{maintenanceData.summary?.healthyGames || 0}</p>
                </div>
              </div>

              {/* Row 2: Issues/Maintenance Counts */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    Incomplete Sync
                  </div>
                  <p className="text-2xl font-bold text-amber-400">{maintenanceData.incompleteSync?.length || 0}</p>
                </div>
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Database className="w-3 h-3 text-blue-500" />
                    Missing Images
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{maintenanceData.missingImages?.length || 0}</p>
                </div>
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Trophy className="w-3 h-3 text-purple-500" />
                    Missing Achievements
                  </div>
                  <p className="text-2xl font-bold text-purple-400">{maintenanceData.missingAchievements?.length || 0}</p>
                </div>
                <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                    <Info className="w-3 h-3 text-orange-500" />
                    Missing Metadata
                  </div>
                  <p className="text-2xl font-bold text-orange-400">{maintenanceData.incompleteMetadata?.length || 0}</p>
                </div>
              </div>
            </>
          ) : null}
          
          {/* Total Issues Summary */}
          {maintenanceData && (
            <div className={`p-3 rounded-lg border ${
              (maintenanceData.summary?.issueCount || 0) > 0
                ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-green-500/10 border-green-500/30'
            }`}>
              <p className={`text-sm font-medium ${
                (maintenanceData.summary?.issueCount || 0) > 0 ? 'text-amber-400' : 'text-green-400'
              }`}>
                {(maintenanceData.summary?.issueCount || 0) > 0
                  ? `${maintenanceData.summary.issueCount} games need attention`
                  : 'All games are healthy!'}
              </p>
            </div>
          )}
          
          <RouterLink to="/library-maintenance">
            <Button
              variant="outline"
              className="w-full border-white/10 text-white/70 hover:bg-white/10 justify-start"
            >
              <Database className="w-4 h-4 mr-2" />
              Library Maintenance
              <span className="ml-auto text-white/40 text-xs">Review &amp; fix issues</span>
            </Button>
          </RouterLink>
        </CardContent>
      </Card>

        </TabsContent>
        <TabsContent value="custom" className="space-y-6 mt-0">
      {/* Gaming Platforms */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-green-400" />
            Gaming Platforms
          </CardTitle>
          <Dialog open={platformDialogOpen} onOpenChange={setPlatformDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-platform-btn"
                className="bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Platform
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add Gaming Platform</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Platform Name</Label>
                  <Input
                    data-testid="platform-name-input"
                    placeholder="e.g., Google Play, Nintendo Switch"
                    value={newPlatform.name}
                    onChange={(e) => setNewPlatform({ ...newPlatform, name: e.target.value })}
                    className="bg-black/50 border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Score Abbreviation (max 3 letters)</Label>
                  <Input
                    data-testid="platform-score-abbr-input"
                    placeholder="e.g., XP, GS, PTS"
                    value={newPlatform.scoreAbbr}
                    onChange={(e) => setNewPlatform({ ...newPlatform, scoreAbbr: e.target.value.toUpperCase().slice(0, 3) })}
                    className="bg-black/50 border-white/10 w-24"
                    maxLength={3}
                  />
                  <p className="text-white/40 text-xs">Used to display scores (e.g., &quot;1000 GS&quot; for Xbox Gamerscore)</p>
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewPlatform({ ...newPlatform, color })}
                        className={`w-8 h-8 rounded-sm transition-all ${
                          newPlatform.color === color ? "ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0A]" : ""
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <Button onClick={handleCreatePlatform} className="w-full bg-green-600 hover:bg-green-700">
                  Create Platform
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Edit Platform Dialog */}
          <Dialog open={!!editingPlatform} onOpenChange={(open) => !open && setEditingPlatform(null)}>
            <DialogContent className="bg-[#0A0A0A] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Edit Platform</DialogTitle>
              </DialogHeader>
              {editingPlatform && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Platform Name</Label>
                    <Input
                      value={editingPlatform.name}
                      onChange={(e) => setEditingPlatform({ ...editingPlatform, name: e.target.value })}
                      className="bg-black/50 border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Score Abbreviation (max 3 letters)</Label>
                    <Input
                      placeholder="e.g., XP, GS, PTS"
                      value={editingPlatform.scoreAbbr || ""}
                      onChange={(e) => setEditingPlatform({ ...editingPlatform, scoreAbbr: e.target.value.toUpperCase().slice(0, 3) })}
                      className="bg-black/50 border-white/10 w-24"
                      maxLength={3}
                    />
                    <p className="text-white/40 text-xs">Used to display scores (e.g., &quot;1000 GS&quot;)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex flex-wrap gap-2">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setEditingPlatform({ ...editingPlatform, color })}
                          className={`w-8 h-8 rounded-sm transition-all ${
                            editingPlatform.color === color ? "ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0A]" : ""
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleUpdatePlatform} className="w-full bg-blue-600 hover:bg-blue-700">
                    Save Changes
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <p className="text-white/40 text-sm mb-4">
            Add custom gaming platforms here. Click the edit button to customize platform settings including score abbreviation. Platforms can only be deleted by an admin in the Master Database.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {platforms.filter((p) => !p.isUserCustom).map((platform) => (
              <div
                key={platform.id}
                className="flex items-center justify-between p-2 sm:p-2 bg-black/30 rounded-lg border border-white/5"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: platform.color }}
                  />
                  <span className="text-white text-sm truncate">{platform.name}</span>
                  {platform.scoreAbbr && (
                    <span className="text-white/40 text-xs flex-shrink-0">({platform.scoreAbbr})</span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 sm:h-6 sm:w-6 text-white/40 hover:text-white hover:bg-white/10 flex-shrink-0"
                    onClick={() => setEditingPlatform({ ...platform })}
                    data-testid={`edit-platform-${platform.id}`}
                  >
                    <Edit2 className="w-4 h-4 sm:w-3 sm:h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Your Custom Platforms & Systems (user-owned) */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            Platforms &amp; Systems You Added
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-white/40 text-sm mb-4">
            Custom platforms and systems you added while adding games. Rename or remove the ones you no longer need — these are private to your account.
          </p>
          {platforms.filter((p) => p.isUserCustom).length === 0 ? (
            <div className="text-center py-8 text-white/30 text-sm" data-testid="no-custom-platforms">
              You haven't added any custom platforms yet. You can add one from the "Add Game" dialog.
            </div>
          ) : (
            <div className="space-y-3">
              {platforms.filter((p) => p.isUserCustom).map((platform) => {
                const customSystems = (systems[platform.id] || []).filter((s) => s.isUserCustom);
                const isEditingPlat = editCustomPlatform.id === platform.id;
                return (
                  <div key={platform.id} className="p-3 bg-black/30 rounded-lg border border-white/5" data-testid={`custom-platform-${platform.id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: platform.color }} />
                      {isEditingPlat ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            autoFocus
                            value={editCustomPlatform.name}
                            onChange={(e) => setEditCustomPlatform({ ...editCustomPlatform, name: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRenameCustomPlatform();
                              if (e.key === "Escape") setEditCustomPlatform({ id: null, name: "" });
                            }}
                            className="bg-black/50 border-white/10 h-8 text-sm flex-1"
                            data-testid={`custom-platform-name-input-${platform.id}`}
                          />
                          <Button size="sm" className="h-8 bg-cyan-600 hover:bg-cyan-500" onClick={handleRenameCustomPlatform} data-testid={`save-custom-platform-${platform.id}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditCustomPlatform({ id: null, name: "" })}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="text-white text-sm font-medium flex-1 truncate">{platform.name}</span>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-white/40 hover:text-white hover:bg-white/10"
                            onClick={() => setEditCustomPlatform({ id: platform.id, name: platform.name })}
                            data-testid={`rename-custom-platform-${platform.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10"
                            onClick={() => openReassignDialog(platform.id, platform.name)}
                            title="Move games to another platform, then remove"
                            data-testid={`reassign-custom-platform-${platform.id}`}
                          >
                            <MoveRight className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-custom-platform-${platform.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">Remove platform?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This removes &quot;{platform.name}&quot; and its custom systems from your account. Games using it must be reassigned or removed first.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-transparent border-white/10">Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteCustomPlatform(platform.id, platform.name)} className="bg-red-500 hover:bg-red-600">
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>

                    {/* Custom systems under this platform */}
                    {customSystems.length > 0 && (
                      <div className="mt-2 ml-5 flex flex-wrap gap-2">
                        {customSystems.map((system) => {
                          const isEditingSys = editCustomSystem.id === system.id;
                          return isEditingSys ? (
                            <div key={system.id} className="flex items-center gap-1">
                              <Input
                                autoFocus
                                value={editCustomSystem.name}
                                onChange={(e) => setEditCustomSystem({ ...editCustomSystem, name: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleRenameCustomSystem();
                                  if (e.key === "Escape") setEditCustomSystem({ id: null, name: "" });
                                }}
                                className="bg-black/50 border-white/10 h-7 text-xs w-40"
                                data-testid={`custom-system-name-input-${system.id}`}
                              />
                              <Button size="icon" className="h-7 w-7 bg-cyan-600 hover:bg-cyan-500" onClick={handleRenameCustomSystem} data-testid={`save-custom-system-${system.id}`}>
                                <CheckCircle2 className="w-3 h-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditCustomSystem({ id: null, name: "" })}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div key={system.id} className="flex items-center gap-1.5 px-2 py-1 bg-black/40 rounded border border-white/10 group" data-testid={`custom-system-${system.id}`}>
                              <span className="text-white text-xs">{system.name}</span>
                              <button
                                onClick={() => setEditCustomSystem({ id: system.id, name: system.name })}
                                className="text-white/20 hover:text-white transition-colors"
                                data-testid={`rename-custom-system-${system.id}`}
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDeleteCustomSystem(system.id, system.name)}
                                className="text-white/20 hover:text-red-400 transition-colors"
                                data-testid={`delete-custom-system-${system.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reassign games off a custom platform, then remove it */}
      <Dialog open={reassign.open} onOpenChange={(open) => !open && setReassign((r) => ({ ...r, open: false }))}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white" data-testid="reassign-platform-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{reassign.count > 0 ? "Move games & remove platform" : "Remove platform"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-white/60 text-sm">
              {reassign.loadingUsage ? (
                <span className="flex items-center gap-2 text-white/40"><Loader2 className="w-4 h-4 animate-spin" />Checking how many games use this platform…</span>
              ) : reassign.count > 0 ? (
                <>
                  <span className="text-white font-medium">{reassign.count}</span> game{reassign.count === 1 ? "" : "s"} currently use
                  {" "}&quot;{reassign.platformName}&quot;. Choose where to move them, then this platform will be removed.
                </>
              ) : (
                <>No games use &quot;{reassign.platformName}&quot;. You can remove it right away.</>
              )}
            </p>

            <div className="space-y-2">
              <Label>Move games to platform</Label>
              <Select
                value={reassign.targetPlatformId}
                onValueChange={(v) => setReassign((r) => ({ ...r, targetPlatformId: v, targetSystem: "" }))}
              >
                <SelectTrigger data-testid="reassign-target-platform" className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select destination platform" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {platforms.filter((p) => p.id !== reassign.platformId).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span style={{ color: p.color }}>{p.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {reassign.targetPlatformId && (systems[reassign.targetPlatformId] || []).length > 0 && (
              <div className="space-y-2">
                <Label>Set system (optional)</Label>
                <Select
                  value={reassign.targetSystem}
                  onValueChange={(v) => setReassign((r) => ({ ...r, targetSystem: v }))}
                >
                  <SelectTrigger data-testid="reassign-target-system" className="bg-black/50 border-white/10">
                    <SelectValue placeholder="Keep as-is / choose a system" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {(systems[reassign.targetPlatformId] || []).map((s) => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/20" onClick={() => setReassign((r) => ({ ...r, open: false }))}>
              Cancel
            </Button>
            <Button
              onClick={handleReassignAndRemove}
              disabled={reassign.submitting || reassign.loadingUsage || !reassign.targetPlatformId}
              className="bg-cyan-600 hover:bg-cyan-500"
              data-testid="reassign-confirm-btn"
            >
              {reassign.submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>{reassign.count > 0 ? `Move ${reassign.count} game${reassign.count === 1 ? "" : "s"} & Remove` : "Remove platform"}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </TabsContent>
        <TabsContent value="linked" className="space-y-6 mt-0">
      {/* Xbox Integration */}
      <Card className="bg-[#0A0A0A] border-[#107C10]/30">
        <CardHeader 
          className="cursor-pointer hover:bg-white/5 rounded-t-lg transition-colors"
          onClick={() => togglePlatformExpanded("xbox")}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <img src="/platform-logos/xbox-green.png" alt="Xbox" className="w-5 h-5 object-contain" />
              Xbox Integration
              {xboxOAuthStatus.authenticated && (
                <span className="text-xs text-[#107C10] font-normal">• Connected</span>
              )}
            </CardTitle>
            {expandedPlatforms.xbox !== false ? (
              <ChevronUp className="w-5 h-5 text-white/50" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white/50" />
            )}
          </div>
          <CardDescription className="text-white/50">
            Connect your Xbox account to sync games and achievements
          </CardDescription>
        </CardHeader>
        {expandedPlatforms.xbox !== false && (
        <CardContent className="space-y-4">
          {/* Xbox OAuth - Primary Method */}
          <div className="p-4 bg-[#107C10]/5 rounded-lg border border-[#107C10]/20">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-[#107C10]"></div>
              <span className="text-white font-medium text-sm">Sign in with Xbox (Recommended)</span>
            </div>
            
            {xboxOAuthStatus.authenticated ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#107C10]/10 rounded-lg border border-[#107C10]/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#107C10] flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-[#107C10] font-bold">{xboxOAuthStatus.gamertag}</p>
                      <p className="text-white/50 text-xs">Signed in via Xbox Live</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#107C10]" />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={handleXboxOAuthDisconnect}
                      data-testid="xbox-oauth-disconnect-btn"
                    >
                      <Unlink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* OAuth Sync Button */}
                <div className="space-y-1">
                  <Button
                    onClick={handleXboxOAuthSync}
                    disabled={xboxSyncing}
                    className="bg-[#107C10] hover:bg-[#0e6b0e] text-white w-full"
                    data-testid="xbox-oauth-sync-btn"
                  >
                    {xboxSyncing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Syncing Full Profile... (this may take a while)
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Sync Full Xbox Profile
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Sync Results Display */}
                {xboxSyncResult && xboxSyncResult.success && (
                  <div className="p-3 bg-[#107C10]/10 border border-[#107C10]/30 rounded-lg space-y-2 text-xs">
                    <p className="text-[#107C10] font-medium">Sync Complete!</p>
                    <div className="grid grid-cols-2 gap-2 text-white/70">
                      <div>Games: <span className="text-white">{xboxSyncResult.total_titles}</span></div>
                      <div>New Games: <span className="text-white">{xboxSyncResult.games_imported}</span></div>
                      <div>New Achievements: <span className="text-white">{xboxSyncResult.achievements_imported}</span></div>
                      <div>Updated: <span className="text-white">{xboxSyncResult.achievements_updated || 0}</span></div>
                    </div>
                    {xboxSyncResult.target_gamerscore > 0 && (
                      <div className="pt-2 border-t border-white/10">
                        <div className="flex justify-between items-center">
                          <span className="text-white/60">Gamerscore Synced:</span>
                          <span className={xboxSyncResult.gamerscore_complete ? "text-[#107C10]" : "text-amber-400"}>
                            {xboxSyncResult.synced_gamerscore?.toLocaleString()} / {xboxSyncResult.target_gamerscore?.toLocaleString()} GS
                          </span>
                        </div>
                        {xboxSyncResult.missing_gamerscore > 0 && (
                          <p className="text-amber-400/80 text-[10px] mt-1">
                            Missing: {xboxSyncResult.missing_gamerscore?.toLocaleString()} GS
                          </p>
                        )}
                      </div>
                    )}
                    {xboxSyncResult.api_limit_note && (
                      <div className="pt-2 border-t border-white/10">
                        <p className="text-amber-400/80 text-[10px]">
                          ⚠️ {xboxSyncResult.api_limit_note}
                        </p>
                      </div>
                    )}
                    {xboxSyncResult.skipped_challenges > 0 && (
                      <div className="pt-2 border-t border-white/10">
                        <p className="text-white/40 text-[10px]">
                          Skipped {xboxSyncResult.skipped_challenges} legacy Xbox Challenge{xboxSyncResult.skipped_challenges === 1 ? "" : "s"} (toggle below to include them next time)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Sync preference: legacy Xbox Challenges */}
                <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02] flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <Label className="text-white text-sm font-medium" htmlFor="include-xbox-challenges">
                      Include legacy Xbox Challenges
                    </Label>
                    <p className="text-white/50 text-xs mt-1">
                      Pull Xbox "Challenge" achievements (0 Gamerscore, time-windowed, discontinued by Xbox). Off by default — turn on if you want to preserve historical records you earned.
                    </p>
                  </div>
                  <Switch
                    id="include-xbox-challenges"
                    data-testid="include-xbox-challenges-switch"
                    checked={includeXboxChallenges}
                    onCheckedChange={async (checked) => {
                      setIncludeXboxChallenges(checked);
                      try {
                        await axios.put(`${API}/settings/preferences`, { includeXboxChallenges: checked });
                        toast.success(checked ? "Xbox Challenges will be included in syncs" : "Xbox Challenges will be skipped");
                      } catch (err) {
                        setIncludeXboxChallenges(!checked);
                        toast.error("Failed to save preference");
                      }
                    }}
                  />
                </div>
                
                {/* Cleanup Duplicates Button */}
                <Button
                  variant="outline"
                  onClick={handleCleanupDuplicates}
                  disabled={cleaningDuplicates}
                  className="w-full border-white/20 text-white/70 hover:text-white"
                  data-testid="cleanup-duplicates-btn"
                >
                  {cleaningDuplicates ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cleaning up duplicates...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Cleanup Duplicate Games
                    </>
                  )}
                </Button>
                <p className="text-white/30 text-xs text-center">
                  Merge duplicate Xbox games with the same title ID
                </p>
                
                <p className="text-white/40 text-xs">
                  ⚠️ For large profiles, Xbox API only returns the 1,000 most recent games. Older titles will need to be manually added or imported via TrueAchievements CSV.
                </p>
                
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-400 text-xs font-medium mb-1">📌 Important: Xbox 360 Games</p>
                  <p className="text-white/60 text-xs">
                    Syncing only imports games you've <strong>recently played while online</strong>. For Xbox 360 games that don't appear, you'll need to:
                  </p>
                  <ol className="text-white/50 text-xs mt-2 ml-4 list-decimal space-y-1">
                    <li>Start the game on your Xbox while connected to Xbox Live</li>
                    <li>Earn at least one achievement (or load your save)</li>
                    <li>Then sync again here</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-white/60 text-sm">
                  Sign in with your Microsoft account to sync your Xbox achievements directly. This method provides better access than the gamertag lookup.
                </p>
                <Button
                  onClick={handleXboxOAuthSignIn}
                  disabled={xboxOAuthLoading}
                  className="w-full bg-[#107C10] hover:bg-[#0e6b0e] text-white font-bold"
                  data-testid="xbox-oauth-signin-btn"
                >
                  {xboxOAuthLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting...
                    </>
                  ) : (
                    <>
                      <img src="/platform-logos/xbox-green.png" alt="Xbox" className="w-4 h-4 mr-2 object-contain" />
                      Sign in with Xbox
                    </>
                  )}
                </Button>
                
                {/* Setup Instructions Accordion */}
                <button
                  onClick={() => setShowXboxSetup(!showXboxSetup)}
                  className="flex items-center gap-2 text-white/50 hover:text-white/70 text-xs transition-colors w-full"
                >
                  <Info className="w-3 h-3" />
                  <span>Getting &quot;redirect_uri is not valid&quot; error?</span>
                  {showXboxSetup ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                </button>
                
                {showXboxSetup && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3 text-xs">
                    <p className="text-amber-200 font-medium">You need to add this Redirect URI to your Azure app:</p>
                    <div className="flex items-center gap-2 bg-black/30 p-2 rounded font-mono text-white/80">
                      <span className="flex-1 break-all">{getApiUrl()}/api/xbox/oauth/callback</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-white/50 hover:text-white shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(`${getApiUrl()}/api/xbox/oauth/callback`);
                          toast.success("Redirect URI copied!");
                        }}
                        data-testid="copy-redirect-uri-btn"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="text-white/60 space-y-2">
                      <p className="font-medium text-white/80">Steps to fix:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-[#107C10] hover:underline inline-flex items-center gap-1">Azure Portal <ExternalLink className="w-3 h-3" /></a></li>
                        <li>Find your Xbox app registration</li>
                        <li>Go to <strong>Authentication</strong> → <strong>Add a platform</strong> → <strong>Web</strong></li>
                        <li>Paste the Redirect URI above</li>
                        <li>Save and try signing in again</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Sync Results */}
          {xboxSyncResult && (
            <div className={`p-3 rounded-lg border ${xboxSyncResult.success !== false ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <div className="flex items-center gap-2 mb-2">
                {xboxSyncResult.success !== false ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                )}
                <span className={xboxSyncResult.success !== false ? "text-green-300" : "text-red-300"}>
                  {xboxSyncResult.message}
                </span>
              </div>
              {xboxSyncResult.games_imported !== undefined && (
                <p className="text-white/60 text-sm">
                  Games: {xboxSyncResult.games_imported} • Achievements: {xboxSyncResult.achievements_imported}
                </p>
              )}
            </div>
          )}
          
          {/* Platform Settings (Toggle + Systems) */}
          {renderPlatformSettings("xbox")}
        </CardContent>
        )}
      </Card>

      {/* Steam ID Setup */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader 
          className="cursor-pointer hover:bg-white/5 rounded-t-lg transition-colors"
          onClick={() => togglePlatformExpanded("steam")}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <img src="/platform-logos/steam.ico" alt="Steam" className="w-5 h-5" />
              Steam Account
              {steamStatus.steamId && (
                <span className="text-xs text-[#66C0F4] font-normal">• Connected</span>
              )}
            </CardTitle>
            {expandedPlatforms.steam !== false ? (
              <ChevronUp className="w-5 h-5 text-white/50" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white/50" />
            )}
          </div>
          <CardDescription className="text-white/60">
            Sync your Steam games and achievements. Requires a public Steam profile.
          </CardDescription>
        </CardHeader>
        {expandedPlatforms.steam !== false && (
        <CardContent className="space-y-4">
          {steamStatus.steamId ? (
            <div className="space-y-4">
              {/* Connected Account */}
              <div className="flex items-center gap-4 p-4 bg-[#1b2838] rounded-lg border border-[#66C0F4]/30">
                <div className="w-12 h-12 rounded-full bg-[#66C0F4] flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[#66C0F4] font-bold">{steamStatus.steamName || "Steam User"}</p>
                  <p className="text-white/50 text-xs">Steam ID: {steamStatus.steamId}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSteamStatus({ ...steamStatus, steamId: null });
                    }}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Change
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleClearSteamId}
                    disabled={steamClearing}
                    data-testid="steam-clear-btn"
                  >
                    {steamClearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Sync Button */}
              <div className="space-y-1">
                <Button
                  onClick={handleSteamProfileSync}
                  disabled={steamSyncing}
                  className="w-full bg-[#1b2838] hover:bg-[#2a475e] text-white border border-[#66C0F4]/30"
                  data-testid="steam-sync-btn"
                >
                  {steamSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Syncing Steam Profile...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Sync Steam Profile
                    </>
                  )}
                </Button>
              </div>

              {/* Sync Results */}
              {steamSyncResult && (
                <div className="p-4 bg-[#1b2838]/50 rounded-lg border border-[#66C0F4]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-[#66C0F4]" />
                    <span className="font-bold text-white">Sync Complete!</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-white/70">Games imported:</div>
                    <div className="text-[#66C0F4] font-bold">{steamSyncResult.gamesImported}</div>
                    <div className="text-white/70">Games updated:</div>
                    <div className="text-[#66C0F4] font-bold">{steamSyncResult.gamesUpdated}</div>
                    <div className="text-white/70">Achievements imported:</div>
                    <div className="text-[#66C0F4] font-bold">{steamSyncResult.achievementsImported}</div>
                    <div className="text-white/70">Achievements updated:</div>
                    <div className="text-[#66C0F4] font-bold">{steamSyncResult.achievementsUpdated}</div>
                  </div>
                  {steamSyncResult.errors?.length > 0 && (
                    <div className="mt-2 text-yellow-500 text-xs">
                      {steamSyncResult.errors.length} error(s) occurred during sync
                    </div>
                  )}
                </div>
              )}

              {/* Info */}
              <div className="p-4 bg-black/30 rounded-lg text-sm text-white/60">
                <p className="font-medium text-white/80 mb-2">What gets synced:</p>
                <ul className="space-y-1">
                  <li>• All owned games from your Steam library</li>
                  <li>• All achievements (locked and unlocked)</li>
                  <li>• Achievement unlock timestamps</li>
                  <li>• Global achievement rarity percentages</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-[#1b2838]/30 rounded-lg border border-[#66C0F4]/20 text-sm text-white/70">
                <p className="font-medium text-white mb-2">How to find your Steam ID:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Go to your Steam profile</li>
                  <li>Click &quot;Edit Profile&quot; → &quot;Privacy Settings&quot;</li>
                  <li>Make sure &quot;Game details&quot; is set to &quot;Public&quot;</li>
                  <li>Your Steam ID is the 17-digit number in your profile URL</li>
                </ol>
                <p className="mt-2 text-white/50 text-xs">
                  Example: steamcommunity.com/profiles/<span className="text-[#66C0F4]">76561198012345678</span>
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter your 17-digit Steam ID"
                  value={steamIdInput}
                  onChange={(e) => setSteamIdInput(e.target.value)}
                  className="bg-black/50 border-white/10"
                  data-testid="steam-id-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveSteamId()}
                />
                <Button
                  onClick={handleSaveSteamId}
                  disabled={steamSaving || !steamIdInput.trim()}
                  className="bg-[#1b2838] hover:bg-[#2a475e] text-white"
                  data-testid="steam-save-btn"
                >
                  {steamSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
              <p className="text-white/40 text-xs">
                Your Steam profile must be public for us to access your game and achievement data.
              </p>
            </div>
          )}
          
          {/* Platform Settings (Toggle + Systems) */}
          {renderPlatformSettings("steam")}
        </CardContent>
        )}
      </Card>

      {/* PlayStation NPSSO Setup (Advanced) */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader 
          className="cursor-pointer hover:bg-white/5 rounded-t-lg transition-colors"
          onClick={() => togglePlatformExpanded("playstation")}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <img src="/platform-logos/playstation.png" alt="PlayStation" className="w-5 h-5 object-contain" />
              PlayStation Account
              <span className="text-xs bg-yellow-600/30 text-yellow-400 px-2 py-0.5 rounded ml-2">
                Advanced
              </span>
              {psnStatus.configured && psnStatus.onlineId && (
                <span className="text-xs text-[#4a90d9] font-normal">• Connected</span>
              )}
            </CardTitle>
            {expandedPlatforms.playstation !== false ? (
              <ChevronUp className="w-5 h-5 text-white/50" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white/50" />
            )}
          </div>
          <CardDescription className="text-white/60">
            Sync your PlayStation trophies using your NPSSO token. Requires technical setup.
          </CardDescription>
        </CardHeader>
        {expandedPlatforms.playstation !== false && (
        <CardContent className="space-y-4">
          {psnStatus.configured && psnStatus.onlineId ? (
            <div className="space-y-4">
              {/* Connected Account */}
              <div className="flex items-center gap-4 p-4 bg-[#003087]/20 rounded-lg border border-[#003087]/30">
                <div className="w-12 h-12 rounded-full bg-[#003087] flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[#4a90d9] font-bold">{psnStatus.onlineId}</p>
                  <p className="text-white/50 text-xs">PlayStation Network</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPsnStatus({ ...psnStatus, onlineId: null });
                    }}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Change
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleClearNpsso}
                    disabled={psnClearing}
                    data-testid="psn-clear-btn"
                  >
                    {psnClearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Sync Button */}
              <div className="space-y-1">
                <Button
                  onClick={handlePsnProfileSync}
                  disabled={psnSyncing}
                  className="w-full bg-[#003087] hover:bg-[#002060] text-white"
                  data-testid="psn-sync-btn"
                >
                  {psnSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Syncing PlayStation Profile...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Sync PlayStation Profile
                    </>
                  )}
                </Button>
              </div>

              {/* Sync Results */}
              {psnSyncResult && (
                <div className="p-4 bg-[#003087]/20 rounded-lg border border-[#003087]/30">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-[#4a90d9]" />
                    <span className="font-bold text-white">Sync Complete!</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-white/70">Games imported:</div>
                    <div className="text-[#4a90d9] font-bold">{psnSyncResult.gamesImported}</div>
                    <div className="text-white/70">Games updated:</div>
                    <div className="text-[#4a90d9] font-bold">{psnSyncResult.gamesUpdated}</div>
                    <div className="text-white/70">Trophies imported:</div>
                    <div className="text-[#4a90d9] font-bold">{psnSyncResult.trophiesImported}</div>
                    <div className="text-white/70">Trophies updated:</div>
                    <div className="text-[#4a90d9] font-bold">{psnSyncResult.trophiesUpdated}</div>
                  </div>
                </div>
              )}

              {/* Token Expiry Warning */}
              <div className="p-3 bg-yellow-900/20 rounded-lg border border-yellow-600/30 text-sm text-yellow-200/80">
                <AlertTriangle className="w-4 h-4 inline mr-2 text-yellow-500" />
                NPSSO tokens expire after a few months. If sync fails, you&apos;ll need to get a fresh token.
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Warning Banner */}
              <div className="p-4 bg-yellow-900/20 rounded-lg border border-yellow-600/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-200">Advanced Setup Required</p>
                    <p className="text-yellow-200/70 text-sm mt-1">
                      This requires extracting a session token from your browser. If you&apos;re not comfortable with this, 
                      consider using <span className="text-yellow-300">TrueTrophies CSV export</span> instead (Import/Export page).
                    </p>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 bg-[#003087]/10 rounded-lg border border-[#003087]/20 text-sm text-white/70">
                <p className="font-medium text-white mb-3">How to get your NPSSO token:</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>Open a <span className="text-[#4a90d9]">private/incognito browser window</span></li>
                  <li>Go to <code className="bg-black/30 px-1 rounded text-xs">store.playstation.com</code> and sign in</li>
                  <li>After signing in, open a new tab and go to:
                    <code className="block bg-black/30 px-2 py-1 rounded text-xs mt-1 text-[#4a90d9]">
                      https://ca.account.sony.com/api/v1/ssocookie
                    </code>
                  </li>
                  <li>Copy the <span className="text-[#4a90d9]">npsso</span> value from the JSON response</li>
                </ol>
                <p className="mt-3 text-white/50 text-xs">
                  The response looks like: {`{"npsso":"YourTokenHere..."}`}
                </p>
              </div>

              {/* Token Input */}
              <div className="space-y-2">
                <Label className="text-white/70">NPSSO Token</Label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="Paste your NPSSO token here"
                    value={npssoInput}
                    onChange={(e) => setNpssoInput(e.target.value)}
                    className="bg-black/50 border-white/10 font-mono text-sm"
                    data-testid="psn-npsso-input"
                  />
                  <Button
                    onClick={handleSaveNpsso}
                    disabled={psnSaving || !npssoInput.trim()}
                    className="bg-[#003087] hover:bg-[#002060] text-white"
                    data-testid="psn-save-btn"
                  >
                    {psnSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Connect"
                    )}
                  </Button>
                </div>
              </div>

              {/* Alternative Option */}
              <div className="p-3 bg-black/30 rounded-lg text-sm text-white/50">
                <p>
                  <span className="text-white/70">Easier alternative:</span> Export your trophies from{" "}
                  <a href="https://www.truetrophies.com" target="_blank" rel="noopener noreferrer" className="text-[#4a90d9] hover:underline">
                    TrueTrophies.com
                  </a>{" "}
                  as CSV and import on the Import/Export page.
                </p>
              </div>
            </div>
          )}
          
          {/* Platform Settings (Toggle + Systems) */}
          {renderPlatformSettings("playstation")}
        </CardContent>
        )}
      </Card>

      {/* Epic Games Integration */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader 
          className="cursor-pointer hover:bg-white/5 rounded-t-lg transition-colors"
          onClick={() => togglePlatformExpanded("epic")}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Epic_Games_logo.svg/1200px-Epic_Games_logo.svg.png" 
                alt="Epic Games" 
                className="w-5 h-5 invert"
              />
              Epic Games Integration
            </CardTitle>
            {expandedPlatforms.epic !== false ? (
              <ChevronUp className="w-5 h-5 text-white/50" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white/50" />
            )}
          </div>
          <CardDescription className="text-white/60">
            Import Epic Games achievements via HTML page source
          </CardDescription>
        </CardHeader>
        {expandedPlatforms.epic !== false && (
        <CardContent>
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Manual Import Only</p>
                  <p className="text-white/70 text-sm mt-1">
                    Epic Games blocks automated access. Use the HTML import feature to add your achievements.
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/10 text-sm text-white/70">
              <p className="font-medium text-white mb-3">How to import Epic achievements:</p>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to <a href="https://store.epicgames.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">store.epicgames.com</a> and sign in</li>
                <li>Navigate to a game&apos;s achievement page</li>
                <li>Right-click and select <span className="text-white">View Page Source</span></li>
                <li>Copy the entire page source (Ctrl+A, Ctrl+C)</li>
                <li>Go to <RouterLink to="/import-export" className="text-cyan-400 hover:underline">Import/Export</RouterLink> and paste in the Epic Games HTML section</li>
              </ol>
            </div>

            {/* Import Button */}
            <RouterLink to="/import-export">
              <Button className="w-full bg-gradient-to-r from-[#2A2A2A] to-[#1A1A1A] hover:from-[#3A3A3A] hover:to-[#2A2A2A] text-white border border-white/10">
                <Download className="w-4 h-4 mr-2" />
                Go to Import/Export
              </Button>
            </RouterLink>

            {/* Manual Alternative */}
            <div className="p-3 bg-black/30 rounded-lg text-sm text-white/50">
              <p>
                <span className="text-white/70">Alternative:</span> You can always add Epic games manually using{" "}
                <span className="text-white">Add Game</span> on the Games page and selecting the Epic Games platform.
              </p>
            </div>
          </div>
          
          {/* Platform Settings (Toggle + Systems) */}
          {renderPlatformSettings("epic")}
        </CardContent>
        )}
      </Card>

      {/* Other Platforms - For non-integrated platforms */}
      {platforms.filter(p => !["xbox", "steam", "playstation", "epic"].includes(p.id)).length > 0 && (
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Gamepad2 className="w-5 h-5 text-white/50" />
              Other Platforms
            </CardTitle>
            <CardDescription className="text-white/60">
              Settings for additional platforms without direct integrations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {platforms.filter(p => !["xbox", "steam", "playstation", "epic"].includes(p.id)).map(platform => (
              <div key={platform.id} className="p-4 bg-black/30 rounded-lg border border-white/5">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-4 h-4 rounded-sm"
                    style={{ backgroundColor: platform.color }}
                  />
                  <span className="text-white font-medium">{platform.name}</span>
                  {platform.scoreAbbr && (
                    <span className="text-white/40 text-xs">({platform.scoreAbbr})</span>
                  )}
                </div>
                {renderPlatformSettings(platform.id)}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

        </TabsContent>
        <TabsContent value="tags" className="space-y-6 mt-0">
      {/* Game Tags — PRO feature */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Tags className="w-5 h-5 text-indigo-400" />
              Game Tags
            </CardTitle>
            <CardDescription className="text-white/50 mt-1">
              Custom tags to organize and filter your game library
            </CardDescription>
          </div>
          <ProGate feature="custom_tags" inline>
            <Dialog open={gameTagDialogOpen} onOpenChange={setGameTagDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-game-tag-btn"
                  className="bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tag
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Create Game Tag</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Tag Name</Label>
                    <Input
                      data-testid="game-tag-name-input"
                      placeholder="e.g., Favorites, Co-op, RPG"
                      value={newGameTag.name}
                      onChange={(e) => setNewGameTag({ ...newGameTag, name: e.target.value })}
                      className="bg-black/50 border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex flex-wrap gap-2">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewGameTag({ ...newGameTag, color })}
                          className={`w-8 h-8 rounded-sm transition-all ${
                            newGameTag.color === color ? "ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0A]" : ""
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleCreateGameTag} className="w-full bg-indigo-600 hover:bg-indigo-700">
                    Create Game Tag
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </ProGate>
        </CardHeader>
        <CardContent>
          {gameTags.length === 0 ? (
            <p className="text-white/40 text-sm">No game tags created yet. Create tags to organize your library.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {gameTags.map((tag) => (
                <div key={tag.id} className="group relative">
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${tag.color}20`, color: tag.color, border: `1px solid ${tag.color}40` }}
                  >
                    {tag.name}
                  </span>
                  <span className="absolute -top-1 -right-1 bg-white/10 text-white/60 text-xs px-1 rounded">
                    {gameTagUsage[tag.id] || 0}
                  </span>
                  <div className="absolute -top-8 right-0 hidden group-hover:flex gap-1 bg-[#1A1A1A] rounded p-1 border border-white/10">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                      onClick={() => handleDeleteGameTag(tag.id, tag.name)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Achievement Tags - Read Only */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-purple-400" />
            Achievement Tags
          </CardTitle>
          <CardDescription className="text-white/50 mt-1">
            Tags applied to achievements across your library
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {sortedTags.map((tag) => (
              <div key={tag.id} className="relative" data-testid={`ach-tag-${tag.id}`}>
                <TagBadge tag={tag} />
                {tagUsage[tag.id] > 0 && (
                  <span className="absolute -top-1 -right-1 bg-white/10 text-white/60 text-xs px-1 rounded">
                    {tagUsage[tag.id]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

        </TabsContent>
        <TabsContent value="general" className="space-y-6 mt-0">
      {/* Gamertags / Multiplayer Identity */}
      <GamertagsSection />

        </TabsContent>
        <TabsContent value="library" className="space-y-6 mt-0">
      {/* Danger Zone */}
      <Card className="bg-[#0A0A0A] border-red-500/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border border-red-500/30 rounded-lg space-y-4">
            <div>
              <h3 className="text-white font-medium flex items-center gap-2">
                <Database className="w-4 h-4" />
                Clear Database
              </h3>
              <p className="text-white/50 text-sm mt-1">
                Permanently delete all games, achievements, and custom data. This cannot be undone.
              </p>
            </div>

            {clearStep === 0 && (
              <Button
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={() => setClearStep(1)}
              >
                Clear All Data
              </Button>
            )}

            {clearStep === 1 && (
              <div className="space-y-4 p-4 bg-red-500/5 rounded-lg border border-red-500/20">
                <p className="text-red-400 text-sm font-medium">
                  Please confirm you understand the consequences:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="understand"
                      checked={clearChecks.understand}
                      onCheckedChange={(checked) =>
                        setClearChecks({ ...clearChecks, understand: checked === true })
                      }
                    />
                    <label htmlFor="understand" className="text-white/70 text-sm">
                      I understand this will delete ALL my data
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="noBackup"
                      checked={clearChecks.noBackup}
                      onCheckedChange={(checked) =>
                        setClearChecks({ ...clearChecks, noBackup: checked === true })
                      }
                    />
                    <label htmlFor="noBackup" className="text-white/70 text-sm">
                      I have exported my data or don&apos;t need a backup
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="permanent"
                      checked={clearChecks.permanent}
                      onCheckedChange={(checked) =>
                        setClearChecks({ ...clearChecks, permanent: checked === true })
                      }
                    />
                    <label htmlFor="permanent" className="text-white/70 text-sm">
                      I understand this action is permanent and cannot be undone
                    </label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-white/10"
                    onClick={() => {
                      setClearStep(0);
                      setClearChecks({ understand: false, noBackup: false, permanent: false });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    disabled={!clearChecks.understand || !clearChecks.noBackup || !clearChecks.permanent}
                    onClick={() => setClearStep(2)}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {clearStep === 2 && (
              <div className="space-y-4 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-red-400 text-sm font-medium">
                  Final confirmation: Type &quot;DELETE ALL DATA&quot; to proceed
                </p>
                <Input
                  value={clearConfirmText}
                  onChange={(e) => setClearConfirmText(e.target.value)}
                  placeholder="DELETE ALL DATA"
                  className="bg-black/50 border-red-500/30 text-white"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-white/10"
                    onClick={() => {
                      setClearStep(0);
                      setClearConfirmText("");
                      setClearChecks({ understand: false, noBackup: false, permanent: false });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    disabled={clearConfirmText !== "DELETE ALL DATA" || clearing}
                    onClick={handleClearDatabase}
                  >
                    {clearing ? "Clearing..." : "Permanently Delete All Data"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
